export interface HomepageModalStyleSettings {
  modal_bg_color?: string
  modal_bg_opacity?: number | string
  modal_text_color?: string
  modal_overlay_color?: string
  modal_overlay_opacity?: number | string
  modal_overlay_blur?: number | string
  modal_padding?: number | string
  modal_border_radius?: number | string
  modal_max_width?: number | string
}

function parseHexColor(hex: string): { r: number; g: number; b: number } | null {
  let normalized = (hex || '').trim().replace('#', '')

  // Expand #rgb → #rrggbb
  if (/^[0-9a-fA-F]{3}$/.test(normalized)) {
    normalized = normalized
      .split('')
      .map((c) => c + c)
      .join('')
  }

  // Strip alpha if #rrggbbaa
  if (/^[0-9a-fA-F]{8}$/.test(normalized)) {
    normalized = normalized.slice(0, 6)
  }

  if (!/^[0-9a-fA-F]{6}$/.test(normalized)) {
    return null
  }

  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16),
  }
}

function clampPercent(value: number | string | undefined, fallback: number): number {
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) {
    return fallback
  }
  return Math.min(100, Math.max(0, parsed))
}

function colorWithOpacity(hex: string, opacityPercent: number, fallbackHex: string): string {
  const rgb = parseHexColor(hex) || parseHexColor(fallbackHex)
  const alpha = opacityPercent / 100

  if (!rgb) {
    return `rgba(255, 255, 255, ${alpha})`
  }

  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`
}

function toPx(value: number | string | undefined, fallback: number): string {
  if (value === undefined || value === null || value === '') {
    return `${fallback}px`
  }

  if (typeof value === 'number') {
    return `${value}px`
  }

  const trimmed = value.trim()
  if (/^\d+(\.\d+)?$/.test(trimmed)) {
    return `${trimmed}px`
  }

  return trimmed
}

export function getHomepageModalStyles(settings: HomepageModalStyleSettings) {
  const overlayOpacity = clampPercent(settings.modal_overlay_opacity, 70)
  const bgOpacity = clampPercent(settings.modal_bg_opacity, 100)
  const blurRaw = Number(settings.modal_overlay_blur ?? 4)
  const blur = Number.isFinite(blurRaw) ? Math.min(30, Math.max(0, blurRaw)) : 4

  const overlayHex = settings.modal_overlay_color || '#000000'
  const rgb = parseHexColor(overlayHex)

  const overlayBackground = rgb
    ? `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${overlayOpacity / 100})`
    : `rgba(0, 0, 0, ${overlayOpacity / 100})`

  const backdropFilter = blur > 0 ? `blur(${blur}px)` : 'none'

  const panelBackground = colorWithOpacity(
    settings.modal_bg_color || '#ffffff',
    bgOpacity,
    '#ffffff',
  )
  const textColor = settings.modal_text_color || '#333333'
  const borderRadius = toPx(settings.modal_border_radius, 20)
  const padding = toPx(settings.modal_padding, 0)

  return {
    overlay: {
      background: overlayBackground,
      backdropFilter,
      WebkitBackdropFilter: backdropFilter,
    },
    container: {
      background: panelBackground,
      backgroundColor: panelBackground,
      color: textColor,
      borderRadius,
      maxWidth: toPx(settings.modal_max_width, 900),
      overflow: 'hidden',
      ['--homepage-modal-bg' as string]: panelBackground,
      ['--homepage-modal-fg' as string]: textColor,
      ['--homepage-modal-radius' as string]: borderRadius,
    },
    content: {
      // Same fill as the shell — no white ring around widgets
      background: panelBackground,
      backgroundColor: panelBackground,
      color: textColor,
      padding,
      borderRadius,
      boxSizing: 'border-box' as const,
      width: '100%',
      minHeight: '100%',
    },
    closeButton: {
      color: textColor,
    },
  }
}
